import type { GameView } from "@numbet/engine";
import type { ClientMessage } from "@numbet/protocol";
import { useMemo } from "react";
import { Avatar, Chips, Footer, Logo, Page } from "../components/ui.tsx";
import { formatChips, formatWithUnit } from "../format.ts";
import { joinNames, t } from "../i18n.ts";
import { navigate } from "../router.ts";

const CONFETTI_COLORS = ["var(--pink)", "var(--cyan)", "var(--yellow)", "var(--purple)"];

function Confetti() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 60 }, (_, i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 1.2,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        rotate: Math.random() * 360,
      })),
    [],
  );
  return (
    <div className="confetti" aria-hidden="true">
      {pieces.map((p, i) => (
        <i key={i} style={{ left: `${p.left}%`, animationDelay: `${p.delay}s`, background: p.color, rotate: `${p.rotate}deg` }} />
      ))}
    </div>
  );
}

interface Highlight {
  title: string;
  who: string;
  detail: string;
}

/** Highlights aus dem öffentlichen Verlauf – nur was alle gesehen haben (E-22). */
function computeHighlights(view: GameView): Highlight[] {
  const name = (id: string) => view.players.find((p) => p.id === id)?.name ?? "?";
  const highlights: Highlight[] = [];

  let best: { playerId: string; estimate: number; ratio: number; hand: (typeof view.history)[number] } | null = null;
  for (const hand of view.history) {
    for (const r of hand.revealed) {
      const ratio = Math.abs(r.estimate - hand.question.answer) / Math.max(Math.abs(hand.question.answer), 1);
      if (!best || ratio < best.ratio) best = { ...r, ratio, hand };
    }
  }
  // Nur zeigen, wenn die beste Schätzung auch wirklich gut war (höchstens 50 % daneben)
  if (best && best.ratio <= 0.5) {
    const q = best.hand.question;
    highlights.push({
      title: t.bestEstimate,
      who: name(best.playerId),
      detail: `${formatWithUnit(best.estimate, q.unit, q.decimals)} statt ${formatWithUnit(q.answer, q.unit, q.decimals)} – „${q.text}“`,
    });
  }

  const biggest = [...view.history].filter((h) => h.winners.length > 0).sort((a, b) => b.pot - a.pot)[0];
  if (biggest) {
    highlights.push({
      title: t.biggestPot,
      who: joinNames(biggest.winners.map(name)),
      detail: `${formatChips(biggest.pot)} Chips – „${biggest.question.text}“`,
    });
  }

  const wins = new Map<string, number>();
  for (const hand of view.history) for (const id of hand.winners) wins.set(id, (wins.get(id) ?? 0) + 1);
  const top = Math.max(0, ...wins.values());
  if (top > 0) {
    const leaders = [...wins].filter(([, n]) => n === top).map(([id]) => name(id));
    highlights.push({ title: t.mostHands, who: joinNames(leaders), detail: t.handsWon(top) });
  }
  return highlights;
}

interface Props {
  view: GameView;
  myId: string;
  send: (message: ClientMessage) => void;
}

export function GameOver({ view, myId, send }: Props) {
  const ranking = view.ranking ?? [];
  const name = (id: string) => view.players.find((p) => p.id === id)?.name ?? "?";
  const winners = ranking.filter((r) => r.rank === 1).map((r) => name(r.playerId));
  const highlights = computeHighlights(view);
  const isHost = view.hostId === myId;

  return (
    <>
      <Confetti />
      <header className="topbar">
        <Logo />
        <span className="muted small">{t.gameOver}</span>
      </header>
      <Page narrow>
        <div className="stack">
          <div style={{ textAlign: "center" }}>
            <h1 className="title-banner">{winners.length > 1 ? t.winners(joinNames(winners)) : t.winner(winners[0] ?? "")}</h1>
          </div>
          <ol className="podium">
            {ranking.map((r) => (
              <li key={r.playerId} className={r.rank === 1 ? "first" : ""}>
                <span className="rank">{t.place(r.rank)}</span>
                <Avatar id={r.playerId} name={name(r.playerId)} avatar={view.players.find((p) => p.id === r.playerId)?.avatar ?? "initials"} />
                <span style={{ fontWeight: 600 }}>
                  {name(r.playerId)} {r.playerId === myId && <span className="muted">({t.you})</span>}
                </span>
                <Chips amount={r.chips} />
              </li>
            ))}
          </ol>

          {highlights.length > 0 && (
            <section>
              <h2>{t.highlights}</h2>
              <div className="highlights">
                {highlights.map((h, i) => (
                  <div key={h.title} className={`content-card ${["edge-pink", "edge-cyan", "edge-yellow"][i % 3]}`}>
                    <span className={`pill ${["pill-pink", "pill-cyan", "pill-yellow"][i % 3]}`}>{h.title}</span>
                    <h3 style={{ margin: "10px 0 4px" }}>{h.who}</h3>
                    <p style={{ margin: 0, color: "var(--ink-muted)" }}>{h.detail}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {isHost ? (
            <button type="button" className="btn btn-primary btn-large btn-block" onClick={() => send({ type: "rematch" })}>
              {t.playAgain}
            </button>
          ) : (
            <p className="panel" style={{ textAlign: "center", margin: 0, fontWeight: 800 }}>
              {t.waitingForRematch}
            </p>
          )}
          <button type="button" className="btn btn-outline" onClick={() => navigate("/")}>
            {t.toHome}
          </button>
        </div>
      </Page>
      <Footer />
    </>
  );
}
